import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, LogOut, Plus, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PricingDialog from "@/components/PricingDialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STARTERS = [
    {
        id: "portfolio",
        label: "Photographer portfolio",
        tag: "GALLERY · DARK",
        prompt:
            "A moody portfolio for a documentary photographer named Ada Rivas. Full-bleed hero image, a 4-column grid of recent series with hover captions, an about section, and a contact form. Dark palette, editorial serif for headings.",
    },
    {
        id: "saas",
        label: "SaaS landing for an AI code tutor",
        tag: "SPLIT · CTA · PRICING",
        prompt:
            "Landing page for 'Kilo', an AI pair-programmer that teaches while you code. Split hero (headline + product mock), 3 feature blocks, testimonial strip, 3-tier pricing ($0 / $12 / $29), FAQ, and a bold CTA to sign up.",
    },
    {
        id: "restaurant",
        label: "One-page restaurant with menu",
        tag: "EDITORIAL · WARM",
        prompt:
            "One-page site for 'Fico', a natural-wine trattoria in Lisbon. Cinematic food image hero, story section, a menu split into snacks / mains / desserts with prices in €, hours, address, Google Maps embed, and a booking button.",
    },
    {
        id: "waitlist",
        label: "Product waitlist page",
        tag: "TERMINAL · MONO",
        prompt:
            "Coming-soon waitlist page for 'Loom Studio' — an AI website builder. Terminal-style monospace hero, email capture form, one-sentence description, three feature highlights, and a marquee of press logos.",
    },
    {
        id: "wedding",
        label: "Wedding invite with RSVP",
        tag: "SOFT · GALLERY",
        prompt:
            "Wedding site for 'Ines & Marco — 12 October 2026'. Elegant serif hero with a date, a story timeline, gallery of photos, venue map, dress code, and an RSVP form with meal choice.",
    },
    {
        id: "blog",
        label: "Personal blog with reading list",
        tag: "MINIMAL · PROSE",
        prompt:
            "A minimalist personal blog for 'Nadia Osei — writing on cities and infrastructure'. A short bio, a list of six recent essays with dates and excerpts, a currently-reading section with three books, and a subscribe form.",
    },
];

function timeAgo(iso) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    const diff = Math.max(1, (Date.now() - t) / 1000);
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

export default function Dashboard() {
    const navigate = useNavigate();
    const { user, billing, loading, logout, refreshBilling } = useAuth();
    const [projects, setProjects] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [creating, setCreating] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [prompt, setPrompt] = useState("");
    const [pricingOpen, setPricingOpen] = useState(false);

    useEffect(() => {
        if (!loading && !user) navigate("/", { replace: true });
    }, [user, loading, navigate]);

    const refresh = async () => {
        try {
            const list = await api.listProjects();
            setProjects(list);
        } catch (e) {
            toast.error(e.message || "Failed to load projects");
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => {
        if (user) refresh();
    }, [user]);

    const startProject = async (initialPrompt) => {
        if (creating) return;
        setCreating(true);
        try {
            const proj = await api.createProject(
                initialPrompt ? initialPrompt.slice(0, 60) : "Untitled site",
                initialPrompt || "",
            );
            navigate(`/builder/${proj.project_id}`, {
                state: initialPrompt ? { initialPrompt } : undefined,
            });
        } catch (e) {
            toast.error(e.message || "Could not create project");
            setCreating(false);
        }
    };

    const removeProject = async (id) => {
        try {
            await api.deleteProject(id);
            setProjects((prev) => prev.filter((p) => p.project_id !== id));
            toast.success("Project deleted");
        } catch (e) {
            toast.error(e.message);
        } finally {
            setConfirmDelete(null);
        }
    };

    const initials = useMemo(() => {
        if (!user?.name) return "◼";
        return user.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
    }, [user]);

    if (loading || !user) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <span className="overline">▍ Loading…</span>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-foreground">
            {/* Top bar */}
            <header className="border-b border-border">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-7 h-7 border border-foreground grid place-items-center">
                            <div className="w-2 h-2 bg-accent" />
                        </div>
                        <span className="font-mono text-[12px] uppercase tracking-[0.2em]">
                            Loom / Dashboard
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            data-testid="open-pricing"
                            onClick={() => setPricingOpen(true)}
                            className={`inline-flex items-center gap-2 h-9 px-3 font-mono text-[10px] uppercase tracking-[0.16em] border transition-colors ${
                                billing?.pro
                                    ? "border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                                    : "border-foreground bg-foreground text-background hover:bg-accent hover:border-accent"
                            }`}
                        >
                            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
                            {billing?.pro ? "Pro" : "Upgrade"}
                        </button>
                        <div className="hidden sm:flex flex-col items-end leading-tight">
                            <span className="font-mono text-[11px] uppercase tracking-[0.2em]">{user.name}</span>
                            <span className="text-[11px] text-muted-foreground">{user.email}</span>
                        </div>
                        <div className="w-9 h-9 border border-border grid place-items-center font-mono text-[11px] uppercase tracking-widest bg-secondary">
                            {initials}
                        </div>
                        <button
                            data-testid="logout-button"
                            onClick={async () => {
                                await logout();
                                navigate("/", { replace: true });
                            }}
                            className="btn-outline-brutal !h-9 !px-3 !text-[10px]"
                        >
                            <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            {/* Prompt-first hero */}
            <section className="border-b border-border">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-14 md:py-20 grid grid-cols-12 gap-8">
                    <div className="col-span-12 md:col-span-5">
                        <div className="overline mb-4">▍ New canvas</div>
                        <h1 className="text-4xl md:text-5xl font-black tracking-tighter leading-[1]">
                            What should we<br />build today?
                        </h1>
                        <p className="mt-4 text-sm text-muted-foreground max-w-md leading-relaxed">
                            Type a sentence. Loom returns a complete, self-contained HTML site you can iterate on.
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-7">
                        <div className="border border-border bg-card shadow-[0_20px_80px_-20px_hsl(14_100%_62%/0.35),0_0_0_1px_hsl(14_100%_62%/0.15)] rounded-md">
                            <div className="border-b border-border px-4 h-9 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                <span>▍ new-project.prompt</span>
                                <span>Claude Sonnet 4.5</span>
                            </div>
                            <textarea
                                data-testid="new-project-prompt"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder="e.g. Portfolio for a Milan-based brutalist architecture studio. Dark, monochrome, IBM Plex Mono, hard edges."
                                rows={5}
                                className="w-full bg-transparent p-5 text-base leading-relaxed resize-y focus:outline-none placeholder:text-muted-foreground"
                            />
                            <div className="border-t border-border p-4 flex flex-wrap items-center justify-between gap-3">
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                    ⌘/Ctrl + Enter to build
                                </div>
                                <button
                                    data-testid="create-project-button"
                                    onClick={() => startProject(prompt.trim())}
                                    disabled={creating}
                                    className="btn-brutal"
                                >
                                    <Plus className="w-4 h-4" strokeWidth={1.5} />
                                    {creating ? "Opening…" : prompt.trim() ? "Build it" : "Start empty"}
                                </button>
                            </div>
                        </div>

                        {/* Starter templates */}
                        <div className="mt-6">
                            <div className="overline mb-3 flex items-center gap-2">
                                <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                                ▍ Or start from an idea
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-border" data-testid="starter-grid">
                                {STARTERS.map((s, i) => (
                                    <button
                                        key={s.id}
                                        data-testid={`starter-${s.id}`}
                                        onClick={() => setPrompt(s.prompt)}
                                        className={`text-left p-4 border-border group hover:bg-secondary transition-colors duration-150 ${
                                            i % 3 !== 2 ? "lg:border-r" : ""
                                        } ${i % 2 !== 1 ? "sm:border-r lg:border-r" : ""} ${
                                            i < STARTERS.length - (STARTERS.length % 3 || 3) ? "lg:border-b" : ""
                                        } border-b sm:last:border-b`}
                                    >
                                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                                            {s.tag}
                                        </div>
                                        <div className="mt-2 font-black tracking-tight text-sm leading-tight">
                                            {s.label}
                                        </div>
                                        <div className="mt-3 flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground group-hover:text-foreground">
                                            Use <ArrowUpRight className="w-3 h-3" strokeWidth={1.5} />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Recent projects */}
            <section>
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-14 md:py-16">
                    <div className="flex items-end justify-between mb-8">
                        <div>
                            <div className="overline mb-2">▍ 002 · Recent work</div>
                            <h2 className="text-2xl md:text-3xl font-black tracking-tight">
                                Your projects <span className="text-muted-foreground font-mono text-base align-middle ml-2">({projects.length})</span>
                            </h2>
                        </div>
                    </div>

                    {loadingList ? (
                        <div className="overline">▍ Loading projects…</div>
                    ) : projects.length === 0 ? (
                        <div className="border border-dashed border-border p-16 text-center">
                            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                                ▍ No canvases yet — write a prompt above
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border border-border">
                            {projects.map((p, i) => (
                                <div
                                    key={p.project_id}
                                    data-testid={`project-card-${p.project_id}`}
                                    className={`group relative p-6 border-border ${
                                        i % 3 !== 2 ? "lg:border-r" : ""
                                    } ${i % 2 !== 1 ? "sm:border-r lg:border-r" : ""} border-b hover:bg-secondary transition-colors duration-150 cursor-pointer`}
                                    onClick={() => navigate(`/builder/${p.project_id}`)}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                            {p.project_id.replace("prj_", "").slice(0, 8)}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            {p.published && (
                                                <span
                                                    data-testid={`views-pill-${p.project_id}`}
                                                    title={`${p.views_total || 0} pageviews`}
                                                    className="inline-flex items-center gap-1 h-5 px-1.5 border border-primary/40 bg-primary/10 text-primary font-mono text-[9px] uppercase tracking-[0.16em] tabular-nums"
                                                >
                                                    <span className="w-1 h-1 rounded-full bg-primary" />
                                                    {(p.views_total || 0).toLocaleString()} {(p.views_total || 0) === 1 ? "view" : "views"}
                                                </span>
                                            )}
                                            <button
                                                data-testid={`delete-project-${p.project_id}`}
                                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setConfirmDelete(p);
                                                }}
                                            >
                                                <Trash2 className="w-4 h-4 text-muted-foreground hover:text-accent" strokeWidth={1.5} />
                                            </button>
                                        </div>
                                    </div>
                                    <h3 className="mt-10 text-lg font-black tracking-tight leading-tight line-clamp-2 min-h-[52px]">
                                        {p.name}
                                    </h3>
                                    <div className="mt-6 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                        <span>▍ {timeAgo(p.updated_at)}</span>
                                        <span className="flex items-center gap-1 group-hover:text-foreground transition-colors">
                                            Open <ArrowUpRight className="w-3 h-3" strokeWidth={1.5} />
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="rounded-none border-foreground">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this canvas?</AlertDialogTitle>
                        <AlertDialogDescription>
                            &ldquo;{confirmDelete?.name}&rdquo; and its full chat history will be permanently removed. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="confirm-delete-button"
                            className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90"
                            onClick={() => confirmDelete && removeProject(confirmDelete.project_id)}
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <PricingDialog
                open={pricingOpen}
                onOpenChange={(o) => {
                    setPricingOpen(o);
                    if (!o) refreshBilling?.();
                }}
                billing={billing}
            />
        </div>
    );
}
